<template>
    <div id="zsiqwidget"></div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'

export default {
    name: 'ZohoChat',
    data() {
        return {
            zohoLoaded: false
        }
    },
    mounted() {
        if (!this.isAuthed) {
            this.loadZohoChat();
        } else {
            // If user is already authenticated, hide any existing widget
            this.hideZohoWidget();
        }
    },
    watch: {
        isAuthed(authed) {
            // Load chat only for non-authenticated users
            if (!authed) {
                this.loadZohoChat();
            } else {
                // Hide widget immediately when user logs in
                this.hideZohoWidget();
            }
        }
    },
    computed: {
        ...GlobalComputed,
    },
    methods: {
        loadZohoChat() {
            // Check if script is already loaded
            if (document.getElementById('zsiqscript')) {
                // Script already loaded, show widget and update visitor info
                this.showZohoWidget();
                setTimeout(() => {
                    this.updateVisitorInfo();
                    this.applyCapacitorStyles();
                }, 500);
                return;
            }

            // Initialize Zoho SalesIQ setup script FIRST (before loading widget)
            window.$zoho = window.$zoho || {};
            window.$zoho.salesiq = window.$zoho.salesiq || {
                ready: () => {
                    // Set visitor information when widget is ready
                    // Add a small delay to ensure widget is fully initialized
                    setTimeout(() => {
                        this.updateVisitorInfo();
                    }, 1000);
                }
            };

            // Load the Zoho SalesIQ widget script
            const script = document.createElement('script');
            script.id = 'zsiqscript';
            script.src = 'https://salesiq.zohopublic.com/widget?wc=siqf7c0a2ba9c9ce261e48cb86de2aa02c70c44f183830b55dd26adbd58f585d760';
            script.defer = true;
            
            // Handle script load errors
            script.onerror = () => {
                console.error('Failed to load Zoho SalesIQ widget script');
            };
            
            script.onload = () => {
                // Widget script loaded, wait for it to initialize
                setTimeout(() => {
                    if (window.$zoho && window.$zoho.salesiq) {
                        this.updateVisitorInfo();
                        this.applyCapacitorStyles();
                        this.zohoLoaded = true;
                        this.$emit('zoho-loaded');
                    }
                }, 1000);
            };
            
            const firstScript = document.getElementsByTagName('script')[0];
            if (firstScript && firstScript.parentNode) {
                firstScript.parentNode.insertBefore(script, firstScript);
            } else {
                document.head.appendChild(script);
            }
        },
        updateVisitorInfo() {
            // Update visitor information for non-authenticated users
            if (!window.$zoho || !window.$zoho.salesiq) {
                return;
            }

            try {
                // Wait for visitor API to be available
                if (typeof window.$zoho.salesiq.visitor === 'undefined') {
                    // Retry after a short delay
                    setTimeout(() => this.updateVisitorInfo(), 500);
                    return;
                }

                // Set visitor name (only if user is logged in, but widget is for non-authed users)
                // For non-authenticated users, we don't set visitor info
                if (this.user && this.user.name) {
                    window.$zoho.salesiq.visitor.name(this.user.name);
                }
                
                // Set visitor email (only if user is logged in)
                if (this.user && this.user.email) {
                    window.$zoho.salesiq.visitor.email(this.user.email);
                }
                
                // Set visitor phone (if available)
                // if (this.user.phone) {
                //     window.$zoho.salesiq.visitor.phone(this.user.phone);
                // }
                
                // Set custom fields if needed
                // if (this.user.id) {
                //     window.$zoho.salesiq.visitor.customfield({
                //         'User ID': this.user.id.toString()
                //     });
                // }

                // Ensure widget is visible and ready to receive messages
                if (window.$zoho.salesiq.show) {
                    window.$zoho.salesiq.show();
                }
            } catch (error) {
                console.warn('Error updating Zoho visitor info:', error);
                // Retry after delay if there was an error
                setTimeout(() => this.updateVisitorInfo(), 1000);
            }
        },
        hideZohoWidget() {
            // Immediately hide DOM elements as fallback
            const hideDOMElements = () => {
                const zohoElements = document.querySelectorAll('#zsiq_float, #zsiq_chatbox, #zsiq_agtpic');
                zohoElements.forEach(el => {
                    if (el) {
                        el.style.display = 'none';
                        el.style.visibility = 'hidden';
                        el.style.opacity = '0';
                    }
                });
            };

            // Hide DOM elements immediately
            hideDOMElements();

            // Try to use Zoho API if available
            if (window.$zoho && window.$zoho.salesiq) {
                try {
                    // Method 1: Use hide() function
                    if (typeof window.$zoho.salesiq.hide === 'function') {
                        window.$zoho.salesiq.hide();
                    }
                    
                    // Method 2: Hide float window
                    if (window.$zoho.salesiq.floatwindow && typeof window.$zoho.salesiq.floatwindow.visible === 'function') {
                        window.$zoho.salesiq.floatwindow.visible('hide');
                    }
                    
                    // Method 3: Close chat window
                    if (window.$zoho.salesiq.chatwindow && typeof window.$zoho.salesiq.chatwindow.close === 'function') {
                        window.$zoho.salesiq.chatwindow.close();
                    }
                } catch (error) {
                    console.warn('Error hiding Zoho widget via API:', error);
                }
            }

            // Retry hiding DOM elements after a short delay to catch any late-rendered elements
            setTimeout(() => {
                hideDOMElements();
            }, 100);

            // Final retry after longer delay
            setTimeout(() => {
                hideDOMElements();
            }, 500);
        },
        showZohoWidget() {
            // Show only the floating button, not the chat window
            const showFloatingButton = () => {
                // Only show the floating button (#zsiq_float), not the chat window (#zsiq_chatbox)
                const floatButton = document.querySelector('#zsiq_float');
                if (floatButton) {
                    floatButton.style.display = '';
                    floatButton.style.visibility = 'visible';
                    floatButton.style.opacity = '1';
                }
            };

            // Show floating button immediately
            showFloatingButton();

            

            // Retry showing floating button after a short delay
            setTimeout(() => {
                showFloatingButton();
            }, 100);
        },
        applyCapacitorStyles() {
            // Only apply custom positioning when running in Capacitor (mobile app)
            if (!this.capRunning) {
                return;
            }

            // Check if style already exists
            if (document.getElementById('zoho-capacitor-styles')) {
                return;
            }

            // Create and inject custom styles for Capacitor
            const style = document.createElement('style');
            style.id = 'zoho-capacitor-styles';
            style.innerHTML = `
                /* Zoho widget positioning adjustments for Capacitor mobile app */
                #zsiq_float {
                    bottom: 100px !important;
                }
                
                #zsiq_agtpic,
                #zsiq_chatbox {
                    bottom: 90px !important;
                }
            `;
            document.head.appendChild(style);
        }
    },
    beforeDestroy() {
        // Hide widget when component is destroyed
        this.hideZohoWidget();
    }
};
</script>

<style scoped>
/* Container div for the widget - no custom styles needed */
</style>

